import { useState } from 'react';
import { US_STATES } from '../../lib/usStates';
import { AlertTriangle, Download, Plus, Save, Trash2 } from 'lucide-react';
import { apiClient, PhysicianLicense } from '../../lib/api';
import { useUser, useAuthStore } from '../../store/useAuthStore';

interface Row {
  state: string;
  licenseNumber: string;
  expiresAt: string; // yyyy-mm-dd for <input type="date">
  source?: 'nppes' | 'self';
}

/** Dates arrive as ISO strings; the date input wants yyyy-mm-dd. */
function toDateInput(value?: string): string {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
}

function isExpired(value: string): boolean {
  if (!value) return false;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return false;
  // Compare by day so a license expiring today doesn't read as expired.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parsed < today;
}

function toRows(licenses: PhysicianLicense[] | undefined): Row[] {
  return (licenses || []).map((l) => ({
    state: (l.state || '').toUpperCase(),
    licenseNumber: l.licenseNumber || '',
    expiresAt: toDateInput(l.expiresAt),
    source: l.source
  }));
}

/**
 * The physician's own state licenses, editable in Settings.
 *
 * These were previously only reachable inside step 1 of the network-gap request
 * wizard, which most accounts can't open — so a clinic that needed to add a
 * state or record a renewal had no way to see the list at all.
 *
 * Saved through its own endpoint rather than the surrounding profile form: the
 * profile form submits every field it holds on every save, and licenses decide
 * which states the storefront ships to and which number lands on the script.
 */
export default function StateLicensesSection() {
  const user = useUser();
  const pd = (user as any)?.physicianData || {};

  const [rows, setRows] = useState<Row[]>(() => toRows(pd.licenses));
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [nppes, setNppes] = useState<Array<{ state: string; licenseNumber?: string }> | null>(null);

  // Seeded from the store once, then owned by this component. Deliberately NOT
  // re-seeded whenever the user object changes: the auth store is refreshed by
  // unrelated actions elsewhere in Settings, and re-seeding on every refresh
  // both discards half-finished edits and tears down the rows mid-interaction.
  // The list is re-seeded explicitly from the server's response after a save.
  const update = (index: number, field: keyof Row, value: string) =>
    setRows((rs) => rs.map((r, i) => (i === index ? { ...r, [field]: value } : r)));

  const addRow = () =>
    setRows((rs) => [...rs, { state: '', licenseNumber: '', expiresAt: '', source: 'self' }]);

  const removeRow = (index: number) => setRows((rs) => rs.filter((_, i) => i !== index));

  const loadNppes = async () => {
    setImporting(true);
    setMessage(null);
    try {
      const res = await apiClient.getMyNppesLicenses();
      if (!res.success || !res.data) {
        setMessage({ type: 'error', text: res.error || 'Could not reach the NPPES registry.' });
        return;
      }
      if (!res.data.found || res.data.licenses.length === 0) {
        setNppes([]);
        setMessage({
          type: 'error',
          text: 'NPPES has no state licenses on file for your NPI. Add them manually below.'
        });
        return;
      }
      setNppes(res.data.licenses);

      // Merge rather than replace: never discard a state the physician added
      // themselves, and never overwrite a number they've already corrected.
      setRows((rs) => {
        const held = new Set(rs.map((r) => r.state.toUpperCase()));
        const additions = res.data!.licenses
          .filter((l) => !held.has(l.state.toUpperCase()))
          .map((l) => ({
            state: l.state.toUpperCase(),
            licenseNumber: l.licenseNumber || '',
            expiresAt: '',
            source: 'nppes' as const
          }));
        return [...rs, ...additions];
      });
    } finally {
      setImporting(false);
    }
  };

  const save = async () => {
    setMessage(null);

    // Validate here as well as server-side so the physician sees which row is
    // wrong instead of a single opaque failure.
    for (const [i, row] of rows.entries()) {
      const position = `License ${i + 1}`;
      if (!row.state.trim()) {
        setMessage({ type: 'error', text: `${position}: choose a state.` });
        return;
      }
      if (!row.licenseNumber.trim()) {
        setMessage({ type: 'error', text: `${position} (${row.state}): enter the license number.` });
        return;
      }
      if (!row.expiresAt) {
        setMessage({ type: 'error', text: `${position} (${row.state}): enter the expiry date.` });
        return;
      }
    }
    const states = rows.map((r) => r.state.toUpperCase());
    if (new Set(states).size !== states.length) {
      setMessage({ type: 'error', text: 'You have the same state listed twice. Keep one entry per state.' });
      return;
    }

    setSaving(true);
    try {
      const res = await apiClient.updateMyLicenses(
        rows.map((r) => ({
          state: r.state.toUpperCase(),
          licenseNumber: r.licenseNumber.trim(),
          expiresAt: r.expiresAt
        }))
      );
      if (!res.success) {
        setMessage({ type: 'error', text: res.error || 'Could not save your licenses.' });
        return;
      }
      // Re-seed from what the server actually stored (states normalized, blanks
      // trimmed) rather than from what was typed.
      const saved = res.data?.licenses;
      if (saved) {
        setRows(toRows(saved));
        // Patch the cached user rather than re-fetching it: getCurrentUser()
        // flips isLoading, and ProtectedRoute swaps the whole page for a
        // spinner while that is true — which would unmount this section and
        // take the confirmation below with it.
        useAuthStore.getState().patchUser({
          physicianData: { ...pd, licenses: saved }
        } as any);
      }
      setMessage({ type: 'success', text: 'Licenses saved.' });
    } finally {
      setSaving(false);
    }
  };

  const expiredCount = rows.filter((r) => isExpired(r.expiresAt)).length;

  return (
    <div className="mt-8 pt-8 border-t border-gray-200">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-medium text-gray-900">State Licenses</h2>
          <p className="mt-1 text-sm text-gray-600 max-w-2xl">
            The states you're licensed to prescribe in. These decide which states your storefront can
            ship to, and the license number printed on each prescription.
          </p>
        </div>
        <button
          type="button"
          onClick={loadNppes}
          disabled={importing}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          <Download className="w-4 h-4 mr-2" />
          {importing ? 'Checking NPPES...' : 'Add from NPPES'}
        </button>
      </div>

      {nppes !== null && nppes.length > 0 && (
        <p className="mt-3 text-sm text-gray-600">
          NPPES lists {nppes.length} state{nppes.length === 1 ? '' : 's'} for your NPI
          {' '}({nppes.map((l) => l.state).join(', ')}). Anything missing has been added below —
          fill in the license number and expiry, then save.
        </p>
      )}

      {expiredCount > 0 && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">
              {expiredCount} license{expiredCount === 1 ? ' has' : 's have'} expired
            </p>
            <p className="mt-0.5">
              Renew and update the expiry date. Expired licenses still cover their state for now, so
              orders keep flowing — but they should be brought current.
            </p>
          </div>
        </div>
      )}

      <div className="mt-4 space-y-3">
        {rows.length === 0 && (
          <p className="text-sm text-gray-500">
            No licenses on file. Add one below, or pull the list from NPPES.
          </p>
        )}

        {rows.map((row, i) => {
          const expired = isExpired(row.expiresAt);
          return (
            <div
              key={i}
              className="grid grid-cols-1 sm:grid-cols-[7rem_1fr_11rem_auto] gap-3 items-start"
            >
              <div>
                <label className="sr-only" htmlFor={`license-state-${i}`}>State</label>
                <select
                  id={`license-state-${i}`}
                  value={row.state}
                  onChange={(e) => update(i, 'state', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">State</option>
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="sr-only" htmlFor={`license-number-${i}`}>License number</label>
                <input
                  id={`license-number-${i}`}
                  type="text"
                  value={row.licenseNumber}
                  onChange={(e) => update(i, 'licenseNumber', e.target.value)}
                  placeholder="License number"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="sr-only" htmlFor={`license-expiry-${i}`}>Expires</label>
                <input
                  id={`license-expiry-${i}`}
                  type="date"
                  value={row.expiresAt}
                  onChange={(e) => update(i, 'expiresAt', e.target.value)}
                  className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    expired ? 'border-amber-400 bg-amber-50 text-amber-900' : 'border-gray-300'
                  }`}
                />
                {expired && <p className="mt-1 text-xs text-amber-700">Expired</p>}
              </div>

              <button
                type="button"
                onClick={() => removeRow(i)}
                aria-label={`Remove ${row.state || 'license'}`}
                className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-gray-500 hover:text-red-600 hover:border-red-300 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={addRow}
        className="mt-3 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700"
      >
        <Plus className="w-4 h-4 mr-1" />
        Add a state
      </button>

      {message && (
        <div
          className={`mt-4 p-3 rounded-md text-sm ${
            message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Licenses'}
        </button>
      </div>
    </div>
  );
}
